import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronsUpDownIcon, LanguagesIcon, LogOutIcon, MonitorIcon, MoonIcon, SunIcon } from "lucide-react";
import { useNavigate } from "react-router";
import type { Language } from "../../../../shared/api.ts";
import { Avatar } from "@/components/ui/avatar.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { setCsrfToken } from "@/lib/http.ts";
import { useCurrentUser } from "@/lib/queries.ts";
import { useTheme, type ThemePreference } from "@/lib/theme.tsx";

/** Signs out with `POST /auth/logout` and goes to `/login`. */
export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api.logout(),
    onSettled: () => {
      setCsrfToken(null);
      queryClient.clear();
      queryClient.setQueryData(queryKeys.me, null);
      void navigate("/login", { replace: true });
    },
  });
}

/** Avatar, name, email, language, theme, sign out. */
export function UserMenu() {
  const me = useCurrentUser();
  const { t, language, setLanguage } = useI18n();
  const { preference, setPreference } = useTheme();
  const signOut = useSignOut();
  const name = me.user.name ?? me.user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t.shell.userMenu}
        className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors outline-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/35 data-[state=open]:bg-sidebar-accent"
      >
        <Avatar name={me.user.name} email={me.user.email} src={me.user.avatarUrl} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold">{name}</span>
          <span className="block truncate text-[11.5px] text-muted-foreground">{me.user.email}</span>
        </span>
        <ChevronsUpDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-[min(16.5rem,calc(100vw-2rem))]">
        <div className="flex items-center gap-2.5 px-2 py-2">
          <Avatar name={me.user.name} email={me.user.email} src={me.user.avatarUrl} className="size-9" />
          <div className="min-w-0">
            <p className="truncate text-[13px] font-semibold">{name}</p>
            <p className="truncate text-[11.5px] text-muted-foreground">{me.user.email}</p>
            <p className="truncate text-[11.5px] text-muted-foreground">{me.tenant.name}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-1.5">
          <LanguagesIcon className="size-3.5" aria-hidden />
          {t.common.language}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup value={language} onValueChange={(value) => setLanguage(value as Language)}>
          <DropdownMenuRadioItem value="uk">{t.common.languages.uk}</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="en">{t.common.languages.en}</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t.common.theme}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={preference} onValueChange={(value) => setPreference(value as ThemePreference)}>
          <DropdownMenuRadioItem value="system">
            <MonitorIcon aria-hidden />
            {t.common.themes.system}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light">
            <SunIcon aria-hidden />
            {t.common.themes.light}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <MoonIcon aria-hidden />
            {t.common.themes.dark}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => signOut.mutate()}>
          <LogOutIcon aria-hidden />
          {t.common.signOut}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
